import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { type FC, type ReactNode, useMemo } from 'react';

import { useAppData, useGetCodes } from '../components/election-result/upload-result/hooks';
import type { ursType } from '../components/election-result/upload-result/utils';
import ErrorBoundary from '../components/error-boundary';
import { CodePeekProvider } from '../components/reusable/code-peek/CodePeekProvider';
import { useCopy } from '../components/reusable/hooks';
import { CopyPropsContext } from '../context/copy-props';
import { GlobalContext } from '../context/global';
import { ImportVoterProvider } from '../context/import-voter';
import { OrganizationContext } from '../context/organization';
import { PanelHistoryContext } from '../context/panel-history';
import { ActionsContext, StatesContext } from '../context/redux';
import { RightSectionContext } from '../context/right-section';
import { RoleActionContext } from '../context/role-actions';
import { ThemeProvider } from '../context/theme';
import useOrganization from '../hooks/organization';
import { usePanelHistory } from '../hooks/panel-history';
import { useRightPanel } from '../hooks/right-section';
import { useRoleActions } from '../hooks/role-actions';
import { useActions, useStoreState } from '../hooks/use-store-state';
import { queryClient } from '../lib/query-client';

interface IAllProviders {
	children?: ReactNode;
}

export const AllProviders: FC<IAllProviders> = ({ children }) => {
	const states = useStoreState();
	const actions = useActions();
	const copyProps = useCopy(actions);

	const { global } = useAppData();

	const rightPanel = useRightPanel();

	const getCodeProps = useGetCodes({ actions, global, states });

	const onStage = (stage: ursType) => {
		global.updateState('stage', stage);
	};

	const contextValue = useMemo(
		() => ({
			global,
			onStage,
			getCodeProps,
		}),
		[global, getCodeProps]
	);

	const roleActionProps = useRoleActions();

	const organizationProps = useOrganization(actions);

	const panelHistoryProps = usePanelHistory(rightPanel);

	return (
		<ErrorBoundary>
			<QueryClientProvider client={queryClient}>
				<ThemeProvider>
					<GlobalContext.Provider value={contextValue}>
						<ActionsContext.Provider value={actions}>
							<StatesContext.Provider value={states}>
								<RightSectionContext.Provider value={rightPanel}>
									<RoleActionContext.Provider value={roleActionProps}>
										<OrganizationContext.Provider value={organizationProps}>
											<PanelHistoryContext.Provider value={panelHistoryProps}>
												<CopyPropsContext.Provider value={copyProps}>
													<CodePeekProvider>
														<ImportVoterProvider>{children}</ImportVoterProvider>
													</CodePeekProvider>
												</CopyPropsContext.Provider>
											</PanelHistoryContext.Provider>
										</OrganizationContext.Provider>
									</RoleActionContext.Provider>
								</RightSectionContext.Provider>
							</StatesContext.Provider>
						</ActionsContext.Provider>
					</GlobalContext.Provider>
				</ThemeProvider>
				{import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
			</QueryClientProvider>
		</ErrorBoundary>
	);
};
